import AuthAPI from '../../gateways/auth';
import CheckoutAPI from '../../gateways/checkout';

import type {
  ConfirmUserOrderResponse,
  ConfirmUserOrderStatus,
  DigitalErrorDTO,
  ReconfirmUserOrderResponse,
  UserOrderConfirmationDTO,
  UserOrderDeliveryDetailsDTO,
  UserOrderPaymentDetailsDTO,
} from '~gateways/ownable.dtos';
import { ConfirmUserOrder } from '~gateways/ownable.dtos';

type IAuthedUpdateOrderObjectBase = {
  productId: number;
  productOptionId?: number;
  quantity: number;
};

export type IAuthedUpdateOrderObjectSavedCardSavedAddressArgs =
  IAuthedUpdateOrderObjectBase & {
    useSavedAddress: true;
    useSavedCard: true;
    type: 'authed';
    paymentDetails: Pick<UserOrderPaymentDetailsDTO, 'savedCardId'>;
    deliveryDetails: Pick<UserOrderDeliveryDetailsDTO, 'savedAddressId'>;
  };

export type IAuthedUpdateOrderObjectSavedCardArgs =
  IAuthedUpdateOrderObjectBase & {
    useSavedAddress: false;
    useSavedCard: true;
    type: 'authed';
    paymentDetails: Pick<UserOrderPaymentDetailsDTO, 'savedCardId'>;
    deliveryDetails: Omit<UserOrderDeliveryDetailsDTO, 'savedAddressId'>;
  };

export type IAuthedUpdateOrderObjectSavedAddressArgs =
  IAuthedUpdateOrderObjectBase & {
    useSavedAddress: true;
    useSavedCard: false;
    type: 'authed';
    paymentDetails: Omit<UserOrderPaymentDetailsDTO, 'savedCardId'>;
    deliveryDetails: Pick<UserOrderDeliveryDetailsDTO, 'savedAddressId'>;
  };

export type IAuthedUpdateOrderObjectArgs = IAuthedUpdateOrderObjectBase & {
  useSavedAddress: false;
  useSavedCard: false;
  type: 'authed';
  paymentDetails: Omit<UserOrderPaymentDetailsDTO, 'savedCardId'>;
  deliveryDetails: Omit<UserOrderDeliveryDetailsDTO, 'savedAddressId'>;
};

export type IGuestUpdateOrderObjectArgs = IAuthedUpdateOrderObjectBase & {
  type: 'guest';
  anonymousUserEmail: string;
  anonymousUserName: string;
  useSavedAddress: false;
  useSavedCard: false;
  paymentDetails: Omit<UserOrderPaymentDetailsDTO, 'savedCardId'>;
  deliveryDetails: Omit<UserOrderDeliveryDetailsDTO, 'savedAddressId'>;
};

type IConstructorArgs = {
  onError?: (errors: DigitalErrorDTO[]) => void;
};

type IOnDoneArgs = {
  onDone?: () => void;
};

type IUpdateOrderObjectArgs = IOnDoneArgs & {
  newObject:
    | IAuthedUpdateOrderObjectArgs
    | IAuthedUpdateOrderObjectSavedAddressArgs
    | IAuthedUpdateOrderObjectSavedCardArgs
    | IAuthedUpdateOrderObjectSavedCardSavedAddressArgs
    | IGuestUpdateOrderObjectArgs;
};

export class OwnableCheckoutHandler {
  private OrderInstance = new ConfirmUserOrder();
  // noinspection TypeScriptFieldCanBeMadeReadonly
  private onError: (errors: DigitalErrorDTO[]) => void = () => {
    /* do nothing */
  };

  public ConfirmOrderStructure:
    | IAuthedUpdateOrderObjectArgs
    | IAuthedUpdateOrderObjectSavedAddressArgs
    | IAuthedUpdateOrderObjectSavedCardArgs
    | IAuthedUpdateOrderObjectSavedCardSavedAddressArgs
    | IGuestUpdateOrderObjectArgs = {
    // @ts-expect-error
    anonymousUserEmail: undefined,
    // @ts-expect-error
    anonymousUserName: undefined,
    // @ts-expect-error
    productId: undefined,
    quantity: 1,
    productOptionId: undefined,
    deliveryDetails: {
      recipientName: undefined,
      street: undefined,
      townOrCity: undefined,
      county: undefined,
      postCode: undefined,
      phoneNumber: undefined,
      saveAddress: undefined,
      saveAddressAs: undefined,
      setAddressAsDefault: undefined,
      savedAddressId: -1,
    },
    paymentDetails: {
      paymentMethodId: undefined,
      saveCard: undefined,
      saveCardAs: undefined,
      setCardAsDefault: undefined,
      savedCardId: -1,
    },
  };

  public errors: DigitalErrorDTO[] = [];
  public clientSecret: string | undefined = undefined;
  public userMessage: string | undefined = undefined;
  public success: boolean | undefined = undefined;
  public status: ConfirmUserOrderStatus | undefined = undefined;
  public orderToken: string | undefined = undefined;
  public orderConfirmation: UserOrderConfirmationDTO | undefined = undefined;

  constructor({ onError }: IConstructorArgs) {
    if (onError) {
      this.onError = () => {
        onError(this.errors);
      };
    }
  }

  /**
   *
   * This updates the object that will be sent to the API during the requests
   *
   * */
  public updateOrderObject({
    onDone = () => {
      /* do nothing */
    },
    newObject,
  }: IUpdateOrderObjectArgs) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type, useSavedAddress, useSavedCard, ...rest } = newObject;

    // @ts-expect-error - this is fine *fire intensifies*
    this.ConfirmOrderStructure = rest;

    Object.entries(rest).forEach(([key, value]) => {
      // @ts-expect-error
      this.OrderInstance[key] = value;
    });
    onDone();
    return this;
  }

  /**
   *
   * This is called after an order is in a 'payment completed' state
   *
   * */
  public async authenticateUserOrder() {
    if (this.orderConfirmation?.orderId) {
      const apiResult = await AuthAPI.AuthenticateUserOrder({
        orderId: this.orderConfirmation?.orderId,
        saveAddress:
          // @ts-expect-error
          this.ConfirmOrderStructure.deliveryDetails?.saveAddress ?? undefined,
        saveAddressAs:
          // @ts-expect-error
          this.ConfirmOrderStructure.deliveryDetails?.saveAddressAs ??
          undefined,
        saveCard:
          // @ts-expect-error
          this.ConfirmOrderStructure.paymentDetails?.saveCard ?? undefined,
        saveCardAs:
          // @ts-expect-error
          this.ConfirmOrderStructure.paymentDetails?.saveCardAs ?? undefined,
        setAddressAsDefault:
          // @ts-expect-error
          this.ConfirmOrderStructure.deliveryDetails?.setAddressAsDefault ??
          undefined,
        setCardAsDefault:
          // @ts-expect-error
          this.ConfirmOrderStructure.paymentDetails?.setCardAsDefault ??
          undefined,
      });
      if (apiResult) {
        const {
          orderConfirmation,
          clientSecret,
          errors,
          status,
          success,
          userMessage,
        } = apiResult;
        this.orderConfirmation = orderConfirmation;
        this.clientSecret = clientSecret;
        this.errors = errors;
        if (errors) {
          this.onError(errors);
        }
        this.status = status;
        this.success = success;
        this.userMessage = userMessage;
      }
    }
    return null;
  }

  /**
   *
   * This is the initial call to send the order to the API
   *
   * */
  public async submitOrder({
    onDone = () => {
      /* do nothing */
    },
  }: IOnDoneArgs): Promise<OwnableCheckoutHandler> {
    const response: ConfirmUserOrderResponse | null | undefined =
      await CheckoutAPI.confirmUserOrder(this.OrderInstance);
    if (response) {
      const {
        errors,
        clientSecret,
        userMessage,
        success,
        status,
        orderToken,
        orderConfirmation,
      } = response;

      this.errors = errors ?? undefined;
      this.clientSecret = clientSecret;
      this.userMessage = userMessage;
      this.success = success;
      this.status = status;
      this.orderToken = orderToken;
      this.orderConfirmation = orderConfirmation;
    }
    onDone();
    return this;
  }

  /**
   *
   * This is called after SCA is failed one or more times, but then succeeds
   *
   * */
  public async reconfirmUserOrder({
    onDone = () => {
      /* do nothing */
    },
  }: IOnDoneArgs): Promise<OwnableCheckoutHandler> {
    if (!this.orderConfirmation?.orderId) {
      return this;
    }
    const response: ReconfirmUserOrderResponse =
      await CheckoutAPI.reconfirmUserOrder({
        deliveryDetails: this.OrderInstance.deliveryDetails,
        orderId: this.orderConfirmation.orderId,
        productOptionId: this.OrderInstance.productOptionId ?? undefined,
        paymentDetails: this.OrderInstance.paymentDetails,
        quantity: this.OrderInstance.quantity,
        anonymousUserEmail: this.OrderInstance.anonymousUserEmail ?? undefined,
        anonymousUserName: this.OrderInstance.anonymousUserName ?? undefined,
      });
    if (response) {
      try {
        const {
          errors,
          clientSecret,
          userMessage,
          success,
          status,
          orderToken,
          orderConfirmation,
        } = response;

        if (errors) {
          this.onError(errors);
        }
        this.errors = errors;
        this.clientSecret = clientSecret;
        this.userMessage = userMessage;
        this.success = success;
        this.status = status;
        this.orderToken = orderToken;
        this.orderConfirmation = orderConfirmation;
      } catch (err) {
        //
      }
    }
    onDone();
    return this;
  }

  /**
   *
   * This should only be called if you need to reset the order structure to its default state
   * Use cases: User exits checkout, or order is completed
   *
   * */
  public resetSingletonStatus() {
    this.ConfirmOrderStructure = {
      // @ts-expect-error
      anonymousUserEmail: undefined,
      // @ts-expect-error
      anonymousUserName: undefined,
      // @ts-expect-error
      productId: undefined,
      quantity: 1,
      productOptionId: undefined,
      deliveryDetails: {
        recipientName: undefined,
        street: undefined,
        townOrCity: undefined,
        county: undefined,
        postCode: undefined,
        phoneNumber: undefined,
        saveAddress: false,
        saveAddressAs: undefined,
        setAddressAsDefault: false,
        savedAddressId: -1,
      },
      paymentDetails: {
        paymentMethodId: undefined,
        saveCard: false,
        saveCardAs: undefined,
        setCardAsDefault: false,
        savedCardId: -1,
      },
    };
    Object.entries(this.ConfirmOrderStructure).forEach(([key, value]) => {
      // @ts-expect-error
      this.OrderInstance[key] = value;
    });
    this.errors = [];
    this.clientSecret = undefined;
    this.userMessage = undefined;
    this.success = undefined;
    this.status = undefined;
    this.orderToken = undefined;
    this.orderConfirmation = undefined;
  }
}
